// __EQUIP_COMPAT__ (2026-10-04) Truck / trailer form <-> database, in the viewer's units.
// Columns are metric (kg, cm); the form shows lb + ft (imperial, the default) or kg + m.
// Pure: tested by sim/equipment.test.mjs.
import { typeInfo, classFields, LB_PER_KG } from './equipmentCompat';

const CM_PER_FT = 30.48;
const blank = (v) => v === null || v === undefined || v === '';
const round = (n, d) => { const f = Math.pow(10, d); return Math.round(Number(n) * f) / f; };

export const unitLabels = (units) => (units === 'metric'
  ? { weight: 'kg', length: 'm' } : { weight: 'lb', length: 'ft' });

// db value -> form string
export const kgToForm = (kg, units) => (blank(kg) ? '' : String(units === 'metric' ? round(kg, 0) : round(Number(kg) * LB_PER_KG, 0)));
export const cmToForm = (cm, units) => (blank(cm) ? '' : String(units === 'metric' ? round(Number(cm) / 100, 2) : round(Number(cm) / CM_PER_FT, 2)));
// form string -> db value (null when blank or not a positive number)
export const formToKg = (v, units) => {
  if (blank(v)) return null; const n = Number(v); if (!Number.isFinite(n) || n <= 0) return null;
  return round(units === 'metric' ? n : n / LB_PER_KG, 2);
};
export const formToCm = (v, units) => {
  if (blank(v)) return null; const n = Number(v); if (!Number.isFinite(n) || n <= 0) return null;
  return round(units === 'metric' ? n * 100 : n * CM_PER_FT, 2);
};

export const SPEC_KEYS = ['gvwr', 'tare', 'payload', 'gcwr', 'deckLength', 'deckWidth', 'insideHeight', 'deckHeight'];

// A trucks / trailers row -> the spec part of the form state.
export function specToForm(row, units) {
  const r = row || {};
  return {
    gvwr: kgToForm(r.max_weight_kg, units),
    tare: kgToForm(r.tare_weight_kg, units),
    payload: kgToForm(r.payload_kg, units),
    gcwr: kgToForm(r.gcwr_kg, units),
    deckLength: cmToForm(r.cargo_length_cm, units),
    deckWidth: cmToForm(r.cargo_width_cm, units),
    insideHeight: cmToForm(r.cargo_height_cm, units),
    deckHeight: cmToForm(r.deck_height_cm, units),
    features: Array.isArray(r.features) ? r.features.slice() : [],
    // __EQUIP_R5__ own half-load caps (gross): total + per axle / axle group
    halfLoadTotal: kgToForm(r.half_load_total_kg, units),
    halfLoadAxles: (Array.isArray(r.half_load_axle_caps) ? r.half_load_axle_caps : [])
      .filter((a) => a && typeof a === 'object').map((a) => ({ axle: String(a.axle || ''), cap: kgToForm(a.cap_kg, units) })),
  };
}

// The spec part of the form -> columns. Fields the type does not use are left
// as they were (not erased), so switching a type back and forth loses nothing.
export function formToSpec(form, kind, units) {
  const f = form || {};
  const out = {
    max_weight_kg: formToKg(f.gvwr, units),
    tare_weight_kg: formToKg(f.tare, units),
    payload_kg: formToKg(f.payload, units),
    cargo_length_cm: formToCm(f.deckLength, units),
    cargo_width_cm: formToCm(f.deckWidth, units),
    cargo_height_cm: formToCm(f.insideHeight, units),
    deck_height_cm: formToCm(f.deckHeight, units),
    features: Array.isArray(f.features) ? f.features.filter(Boolean) : [],
    half_load_total_kg: formToKg(f.halfLoadTotal, units),
    half_load_axle_caps: (Array.isArray(f.halfLoadAxles) ? f.halfLoadAxles : [])
      .map((a) => ({ axle: String((a && a.axle) || '').trim(), cap_kg: formToKg(a && a.cap, units) }))
      .filter((a) => a.cap_kg !== null),
  };
  if (kind === 'truck') out.gcwr_kg = formToKg(f.gcwr, units);
  return out;
}

// When a NEW record's type is picked: the type's usual deck height and features,
// only into fields still empty.
export function applyTypeDefaults(form, kind, type, units) {
  const info = typeInfo(kind, type);
  const next = { ...form };
  if (info.deckHeightFt && blank(next.deckHeight)) {
    next.deckHeight = String(units === 'metric' ? round(info.deckHeightFt * 0.3048, 2) : info.deckHeightFt);
  }
  if (info.features && (!next.features || !next.features.length)) next.features = info.features.slice();
  return next;
}

// What the compatibility check will be missing for this record (shown on the list
// and the form so the gaps get filled).
export function missingSpecs(row, kind) {
  const r = row || {};
  const info = typeInfo(kind, kind === 'trailer' ? r.trailer_type : r.truck_type);
  const show = classFields(info.cls);
  const miss = [];
  const has = (v) => !blank(v) && Number(v) > 0;
  const payloadKnown = has(r.payload_kg) || (has(r.max_weight_kg) && has(r.tare_weight_kg));
  if (info.cls === 'tractor') {
    if (!has(r.gcwr_kg)) miss.push('GCWR');
    if (!has(r.tare_weight_kg)) miss.push('tare');
  } else if (!payloadKnown) miss.push('payload (or GVWR + tare)');
  if (show.deck && (!has(r.cargo_length_cm) || !has(r.cargo_width_cm))) miss.push(info.cls === 'enclosed' ? 'inside length/width' : 'deck length/width');
  if (show.height && !has(r.cargo_height_cm)) miss.push('inside height');
  if (show.deckHeight && !has(r.deck_height_cm)) miss.push('deck height');
  return miss;
}
